import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useDarkMode } from '@halo/web-lib';
import { RefreshCw } from 'lucide-react';
import { ViewManager } from './dashboard/ViewManager';
import { SectionEditor } from './dashboard/SectionEditor';
import { ComparisonToggle } from './ComparisonToggle';
import { DesktopSidebar } from './DesktopSidebar';
import { MobileNavbar } from './MobileNavbar';
import { LogoutPopup } from './LogoutPopup';
import { DatePickerWithRange } from './DatePickerWithRange';
import { useDateRange } from '../context/DateRangeContext';
import { EmailVerificationBanner } from './EmailVerificationBanner';
import { PasswordSetupBanner } from './PasswordSetupBanner';
import { SetupBanner } from './SetupBanner';
import type { ReactNode } from 'react';

/** Pages that show the date picker header bar */
const DATE_PICKER_PAGES = ['/', '/orders', '/campaigns', '/halo-effect', '/halo-effect-v2', '/debug-data'];

export function Layout({ children }: { children: ReactNode }) {
  const { logout } = useAuth();
  const { isDarkMode } = useDarkMode();
  const { dateRange, globalRefresh } = useDateRange();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);

  const handleLogout = () => {
    void logout();
    navigate('/login');
  };

  const handleSwitchStore = () => {
    navigate('/select-store');
  };

  const showDatePicker = DATE_PICKER_PAGES.some(
    p => p === '/' ? location.pathname === '/' : location.pathname.startsWith(p),
  );

  return (
    <div className="flex min-h-screen bg-neutral-1000 dark:bg-grey-900">
      <DesktopSidebar
        onSwitchStore={handleSwitchStore}
        onLogout={() => setShowLogoutPopup(true)}
      />
      <MobileNavbar
        onSwitchStore={handleSwitchStore}
        onLogout={() => setShowLogoutPopup(true)}
      />

      {/* Main Content */}
      <main className="flex-1 min-w-0 pt-0 md:pt-0 mt-12 md:mt-0">
        <EmailVerificationBanner />
        <PasswordSetupBanner />
        <SetupBanner />
        {/* Sticky content header with date picker */}
        {showDatePicker && (
          <div
            className={`w-full sticky top-12 md:top-0 p-3 z-30 border-b shadow-sm ${
              isDarkMode
                ? 'bg-grey-900 border-grey-1000'
                : 'bg-white border-grey-50'
            }`}
          >
            <div className="w-11/12 mx-auto flex flex-row items-center justify-between gap-3 max-md:flex-col max-md:items-stretch max-md:gap-2">
              {/* Page title */}
              <h1 className="text-lg font-semibold tracking-tight text-charcoalGray-800 dark:text-white max-md:hidden">
                The OmniChannel Decision Hub
              </h1>

              <div className="flex items-center gap-3 ml-auto max-md:w-full max-md:ml-0 max-md:flex-col max-md:gap-2">
                {/* Row 1 (mobile): View / Sections / Refresh */}
                <div className="flex items-center gap-2 max-md:w-full max-md:justify-between">
                  {location.pathname === '/' && (
                    <>
                      <ViewManager />
                      <SectionEditor />
                    </>
                  )}
                  <button
                    onClick={() => {
                      globalRefresh();
                      setIsRefreshing(true);
                      setTimeout(() => setIsRefreshing(false), 1000);
                    }}
                    title="Refresh all data"
                    className="p-2 rounded-lg transition-colors hover:bg-grey-100 dark:hover:bg-grey-800 text-charcoalGray-600 dark:text-grey-300"
                  >
                    <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
                  </button>
                </div>

                {/* Row 2 (mobile): DatePicker on top, Compare beneath — both full width.
                    Desktop: side-by-side. */}
                <div className="flex items-stretch gap-2 max-md:w-full max-md:flex-col">
                  <div className="w-[280px] max-md:w-full">
                    <DatePickerWithRange />
                  </div>
                  <div className="w-[280px] max-md:w-full">
                    <ComparisonToggle />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>

      <LogoutPopup
        isOpen={showLogoutPopup}
        closePopup={() => setShowLogoutPopup(false)}
        handleLogout={handleLogout}
      />
    </div>
  );
}
